import { Link } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen" data-testid="privacy-policy-page">
      <div className="border-b border-[#09090B] py-8">
        <div className="container-swiss">
          <p className="mono-data text-[#09090B] mb-1">Legal</p>
          <h1 className="text-3xl md:text-4xl font-black tracking-tighter flex items-center gap-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <ShieldCheck className="w-8 h-8 text-[#09090B]" />
            Privacy Policy
          </h1>
          <p className="mono-data text-xs text-zinc-500 mt-2">Last updated: April 30, 2026</p>
        </div>
      </div>

      <div className="container-swiss py-8 max-w-3xl">
        <div className="prose prose-zinc max-w-none space-y-6 text-sm leading-relaxed text-zinc-700">
          <section>
            <h2 className="text-lg font-bold mt-0 mb-3 text-black">1. Who We Are</h2>
            <p>
              FOKYES ("we", "us", "the Platform") is an independent cognitive training platform.
              We are the data fiduciary (under India's Digital Personal Data Protection Act, 2023 — "DPDP")
              and controller (under GDPR) for personal data you provide.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold mb-3 text-black">2. Data We Collect</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Account:</strong> name, email, Google profile picture (via OAuth)</li>
              <li><strong>Age & consent:</strong> date of birth, jurisdiction, consent timestamps and IP</li>
              <li><strong>Cognitive data:</strong> session attempts, scores, accuracy, time spent, BrainMap dimensions</li>
              <li><strong>Parent/child links:</strong> if you're a parent, your child's account reference</li>
              <li><strong>Screen-time logs:</strong> voluntarily logged by parents for correlation analysis</li>
              <li><strong>Technical:</strong> cookies (session token), IP for consent audit trail</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold mb-3 text-black">3. Children's Privacy (Under 18)</h2>
            <p>
              We treat any user under 18 as a child and require <strong>verifiable parental consent</strong>
              before processing any personal data. This satisfies:
            </p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li><strong>DPDP Act 2023 (India)</strong> — Section 9 parental consent for under-18s</li>
              <li><strong>COPPA (USA)</strong> — parental consent for under-13s</li>
              <li><strong>GDPR-K (EU)</strong> — parental consent for under-16s</li>
            </ul>
            <p className="mt-3">
              Parents can at any time: review their child's data, request deletion, or revoke consent
              via the Parent Dashboard or by emailing <a href="mailto:privacy@storytellercharles.com" className="text-[#09090B] underline">privacy@storytellercharles.com</a>.
              We do <strong>not</strong> show behavioural or targeted ads to children.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold mb-3 text-black">4. How We Use Your Data</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>Deliver cognitive training sessions and calculate your BrainMap Score</li>
              <li>Adapt difficulty using AI (OpenAI API — data sent is session performance only, not identity)</li>
              <li>Enable parent monitoring (only for linked parents with consent)</li>
              <li>Send transactional email (welcome, parent consent, weekly digest if opted in)</li>
              <li>Improve platform and aggregate (de-identified) peer benchmarks</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold mb-3 text-black">5. Your Rights</h2>
            <p>Under DPDP, COPPA, and GDPR, you (or a parent on a child's behalf) may:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li><strong>Access</strong> — export all your data (Settings → Export)</li>
              <li><strong>Correct</strong> — edit profile from Settings</li>
              <li><strong>Delete</strong> — erase account and all linked data (Settings → Delete Account)</li>
              <li><strong>Withdraw consent</strong> — contact us anytime</li>
              <li><strong>Lodge a complaint</strong> — with your national data protection authority</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold mb-3 text-black">6. Retention</h2>
            <p>
              We retain your data while your account is active. On deletion, personal data is removed within 30 days.
              Consent audit logs (IP + timestamp) are retained for 7 years for legal defense as permitted under DPDP Rule 5.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold mb-3 text-black">7. Security</h2>
            <p>Data is encrypted in transit (TLS). Access is restricted and session tokens expire in 7 days.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold mb-3 text-black">8. Third Parties</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>Google (OAuth sign-in) — <a href="https://policies.google.com/privacy" className="text-[#09090B] underline" target="_blank" rel="noreferrer">their policy</a></li>
              <li>OpenAI (anonymised session data for adaptive AI)</li>
              <li>MongoDB (managed database hosting)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold mb-3 text-black">9. Contact</h2>
            <p>
              Grievance Officer, FOKYES —{' '}
              <a href="mailto:privacy@storytellercharles.com" className="text-[#09090B] underline">privacy@storytellercharles.com</a>
            </p>
          </section>

          <section className="pt-4 border-t border-zinc-200">
            <p className="text-xs text-zinc-500">
              See also: <Link to="/terms" className="text-[#09090B] underline">Terms of Service</Link>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
